import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Pagination } from './ui/pagination';
import { 
  Search, 
  Filter, 
  Download, 
  RotateCcw, 
  Clock,
  CheckSquare,
  FileSpreadsheet,
  Package,
  ArrowRight,
  X,
  Building2,
  Eye,
  Edit,
  Loader2,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  FileText,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  wipApiService, 
  WorkInProgressSummary
} from '../services/wipApi';
import { WorkOrderPriority } from '../services/workOrderApi';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { workAreaApiService, WorkArea } from '../services/workAreaApi';
import { useBranch } from '../contexts/BranchContext';

// Use the WorkInProgressSummary from the API directly
type WIPTask = WorkInProgressSummary;

export function WIPTrackingPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [selectedEndGoods, setSelectedEndGoods] = useState<number[]>([]);
  const [endGoodSearch, setEndGoodSearch] = useState('');
  const [workOrderNumberSearch, setWorkOrderNumberSearch] = useState('');
  const [debouncedWorkOrderNumberSearch, setDebouncedWorkOrderNumberSearch] = useState('');
  const [showEndGoodDropdown, setShowEndGoodDropdown] = useState(false);
  const [selectedWorkAreaId, setSelectedWorkAreaId] = useState<number | ''>('');
  
  // Refs for click-outside detection
  const endGoodDropdownRef = useRef<HTMLDivElement>(null);
  
  // Data state
  const [wipTasks, setWipTasks] = useState<WIPTask[]>([]);
  const [endGoods, setEndGoods] = useState<Endgood[]>([]);
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  
  // Table state
  const [filteredTasks, setFilteredTasks] = useState<WIPTask[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]); // Changed to string[] to store unique keys
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage, setTasksPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filter functions
  const filteredEndGoods = endGoods.filter(endGood =>
    (endGood.endgoodName?.toLowerCase() || '').includes(endGoodSearch.toLowerCase())  );

  // Auto-apply filters when Work Area selection changes (so table updates without clicking Search)
  const prevWorkAreaRef = useRef<number | ''>(selectedWorkAreaId);
  useEffect(() => {
    if (prevWorkAreaRef.current !== selectedWorkAreaId) {
      prevWorkAreaRef.current = selectedWorkAreaId;
      if (wipTasks.length > 0) handleSearch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when work area selection changes
  }, [selectedWorkAreaId]);

  const handleEndGoodSelect = (endGoodId: number) => {
    setSelectedEndGoods(prev => 
      prev.includes(endGoodId) 
        ? prev.filter(id => id !== endGoodId)
        : [...prev, endGoodId]
    );
  };

  const removeEndGoodFilter = (endGoodId: number) => {
    setSelectedEndGoods(prev => prev.filter(id => id !== endGoodId));
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (endGoodDropdownRef.current && !endGoodDropdownRef.current.contains(event.target as Node)) {
        setShowEndGoodDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    if (!selectedBranch) {
      setIsLoadingData(false);
      return;
    }
    
    setIsLoadingData(true);
    try {
      const wipResponse = await wipApiService.getAllWIPTasks(undefined, selectedBranch).catch(() => {
        return { success: false, data: [] as WIPTask[] };
      });

      // Fetch all endgoods, work areas, and processes
      let allEndGoods: Endgood[] = [];
      try {
        const endGoodsResponse = await endgoodApiService.getEndgoodsIdName();
        if (endGoodsResponse.success && Array.isArray(endGoodsResponse.data)) {
          allEndGoods = endGoodsResponse.data;
        }
      } catch (error) {
        // console.error('Error loading endgoods:', error);
      }

      const branchIdNum = typeof selectedBranch === 'string' ? parseInt(selectedBranch, 10) : selectedBranch;
      try {
        const workAreasRes = await workAreaApiService.getActiveByBranch(branchIdNum);
        setWorkAreas(Array.isArray(workAreasRes) ? workAreasRes : []);
      } catch (error) {
        setWorkAreas([]);
      }

      if (wipResponse.success && Array.isArray(wipResponse.data)) {
        setWipTasks(wipResponse.data);
        setFilteredTasks(wipResponse.data);
      }
      
      if (allEndGoods.length > 0) {
        setEndGoods(allEndGoods);
      }

    } catch (error) {
      // console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoadingData(false);
    }
  }, [selectedBranch]);

  // WIP tasks are now loaded directly from the API

  const handleSearch = async () => {
    setIsLoading(true);
    
    try {
      let filtered = [...wipTasks];
      
      // Apply end good filter
      if (selectedEndGoods.length > 0) {
        const selectedEndGoodNames = selectedEndGoods.map(id => 
          endGoods.find(endGood => endGood.id === id)?.endgoodName
        ).filter(Boolean);
        
        filtered = filtered.filter(task => 
          selectedEndGoodNames.includes(task.endgoodName)
        );
      }
      
      // Apply work order number filter (LIKE search)
      // Use the current search value (not debounced) when user clicks Search button
      if (workOrderNumberSearch && workOrderNumberSearch.trim()) {
        const searchTerm = workOrderNumberSearch.trim().toLowerCase();
        filtered = filtered.filter(task => 
          task.workOrderNumber.toLowerCase().includes(searchTerm)
        );
      }

      // Apply work area filter (next process work area); coerce to number in case API returns string
      if (selectedWorkAreaId !== '') {
        const workAreaIdNum = Number(selectedWorkAreaId);
        filtered = filtered.filter(task => {
          const taskWorkAreaId = task.nextProcessWorkAreaId != null ? Number(task.nextProcessWorkAreaId) : null;
          return taskWorkAreaId != null && taskWorkAreaId === workAreaIdNum;
        });
      }
      
      // Client-side filtering
      // Date filtering can be implemented later when needed
      setFilteredTasks(filtered);
      
      setCurrentPage(1);
      toast.success(`Found ${filtered.length} WIP tasks`);
    } catch (error) {
      // console.error('Error searching WIP tasks:', error);
      toast.error('Failed to search WIP tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedEndGoods([]);
    setEndGoodSearch('');
    setWorkOrderNumberSearch('');
    setDebouncedWorkOrderNumberSearch('');
    setSelectedWorkAreaId('');
    setFilteredTasks(wipTasks);
    setSelectedTasks([]);
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection('asc');
    toast.info('Filters reset');
  };

  const handleSelectAll = (checked: boolean) => {
    // console.log('handleSelectAll called:', { checked, currentPageTaskKeys: paginatedTasks.map((task, index) => `${task.workOrderId}-${task.processStepId}-${index}`) });
    const currentPageTaskKeys = paginatedTasks.map((task, index) => `${task.workOrderId}-${task.processStepId}-${index}`);
    
    if (checked) {
      // Select all current page tasks (keep existing selections from other pages)
      setSelectedTasks(prev => {
        const newSelection = [...prev];
        currentPageTaskKeys.forEach(key => {
          if (!newSelection.includes(key)) {
            newSelection.push(key);
          }
        });
        return newSelection;
      });
    } else {
      // Deselect all current page tasks
      setSelectedTasks(prev => prev.filter(key => !currentPageTaskKeys.includes(key)));
    }
  };

  const handleTaskSelect = (taskKey: string, checked: boolean) => {
    // console.log('handleTaskSelect called:', { taskKey, checked });
    if (checked) {
      setSelectedTasks(prev => 
        prev.includes(taskKey) ? prev : [...prev, taskKey]
      );
    } else {
      setSelectedTasks(prev => prev.filter(key => key !== taskKey));
    }
  };

  const handleViewWorkOrder = (workOrderId: number) => {
    navigate(`/work-orders/${workOrderId}/view`);
  };

  const handleEditWorkOrder = (workOrderId: number) => {
    navigate(`/work-orders/${workOrderId}/edit`);
  };

  // Sorting logic
  const sortedTasks = React.useMemo(() => {
    if (!sortColumn) return filteredTasks;

    return [...filteredTasks].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'workOrderNumber':
          aValue = a.workOrderNumber || '';
          bValue = b.workOrderNumber || '';
          break;
        case 'salesOrderNumber':
          aValue = a.salesOrderNumber || '';
          bValue = b.salesOrderNumber || '';
          break;
        case 'endgoodName':
          aValue = a.endgoodName || '';
          bValue = b.endgoodName || '';
          break;
        case 'processName':
          aValue = a.processName || '';
          bValue = b.processName || '';
          break;
        case 'wipQuantity':
          aValue = a.wipQuantity || 0;
          bValue = b.wipQuantity || 0;
          break;
        case 'nextProcessName':
          aValue = a.nextProcessName || '';
          bValue = b.nextProcessName || '';
          break;
        case 'status':
          // Status is derived from isOverdue and isReadyForProduction
          if (a.isOverdue) aValue = 0; // Critical
          else if (a.isReadyForProduction) aValue = 1; // On Track
          else aValue = 2; // Delayed
          
          if (b.isOverdue) bValue = 0; // Critical
          else if (b.isReadyForProduction) bValue = 1; // On Track
          else bValue = 2; // Delayed
          break;
        case 'priority':
          const priorityOrder = { [WorkOrderPriority.HIGH]: 0, [WorkOrderPriority.MEDIUM]: 1, [WorkOrderPriority.LOW]: 2 };
          aValue = priorityOrder[a.priority] ?? 3;
          bValue = priorityOrder[b.priority] ?? 3;
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle number comparison
      return sortDirection === 'asc' 
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  }, [filteredTasks, sortColumn, sortDirection]);

  const handleExport = () => {
    toast.success('Exporting WIP tasks to Excel...');

    const dataToExport = sortColumn ? sortedTasks : filteredTasks;

    const escapeCsvCell = (value: unknown) => {
      const str = value == null ? '' : String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = ['Work Order No', 'Production Date', 'Sales Order', 'End Good Name', 'Process Name', 'WIP Qty', 'Next Process', 'Status', 'Priority'];
    const csvContent = [
      headers.map(escapeCsvCell).join(','),
      ...dataToExport.map((task) =>
        [
          task.workOrderNumber ?? '',
          task.productionDate ? new Date(task.productionDate).toLocaleDateString() : 'N/A',
          task.salesOrderNumber ?? '',
          task.endgoodName ?? '',
          task.processName ?? '',
          task.wipQuantity ?? '',
          task.nextProcessName ?? '',
          task.isOverdue ? 'Critical' : task.isReadyForProduction ? 'On Track' : 'Delayed',
          task.priority ?? '',
        ].map(escapeCsvCell).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wip-tasks-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Pagination logic
  const totalPages = Math.ceil(sortedTasks.length / tasksPerPage);
  const startIndex = (currentPage - 1) * tasksPerPage;
  const paginatedTasks = sortedTasks.slice(startIndex, startIndex + tasksPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when sort changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  // Status badge function removed since WIP API doesn't have work order status

  const getPriorityBadge = (priority: WorkOrderPriority) => {
    const priorityColors = {
      [WorkOrderPriority.HIGH]: 'bg-red-100 text-red-800 border-red-200',
      [WorkOrderPriority.MEDIUM]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      [WorkOrderPriority.LOW]: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Load data on component mount and when branch changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoadingData) {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading WIP data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[95vw] mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
          WIP Tracking Dashboard
        </h1>
        <p className="text-gray-600">Monitor and manage all work-in-progress tasks across your manufacturing operations</p>
      </div>

      {/* Filters Section */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <CardTitle className="flex items-center gap-2 text-primary-800">
            <FileText className="h-5 w-5 text-primary-700" />
            Advanced Filters
          </CardTitle>
          <CardDescription>
            Apply filters to find specific WIP tasks.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {/* All Filters in One Row */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
            {/* End Good Filter */}
            <div className="lg:col-span-3">
              <Label className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-primary-600" />
                End Goods
              </Label>
              <div className="relative" ref={endGoodDropdownRef}>
                <Input
                  value={endGoodSearch}
                  onChange={(e) => setEndGoodSearch(e.target.value)}
                  onFocus={() => setShowEndGoodDropdown(true)}
                  placeholder="Search end goods..."
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
                
                {showEndGoodDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-primary-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredEndGoods.map((endGood) => (
                      <div
                        key={endGood.id}
                        className="flex items-center gap-2 p-2 hover:bg-primary-50 cursor-pointer"
                        onClick={() => handleEndGoodSelect(endGood.id)}
                      >
                        <Checkbox
                          checked={selectedEndGoods.includes(endGood.id)}
                          className="border-primary-300"
                        />
                        <span className="text-sm">{endGood.endgoodName}{endGood.sku ? ` (${endGood.sku})` : ''}</span>
                      </div>
                    ))}
                    <div className="p-2 border-t border-primary-100">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowEndGoodDropdown(false)}
                        className="w-full text-primary-600 hover:text-primary-700"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Work Order Number Filter */}
            <div className="lg:col-span-3">
              <Label className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-primary-600" />
                Work Order Number
              </Label>
              <Input
                value={workOrderNumberSearch}
                onChange={(e) => setWorkOrderNumberSearch(e.target.value)}
                placeholder="Search work order number..."
                className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>

            {/* Work Area Filter */}
            <div className="lg:col-span-3">
              <Label className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-primary-600" />
                Work Area
              </Label>
              <Select
                value={selectedWorkAreaId === '' ? 'all' : String(selectedWorkAreaId)}
                onValueChange={(v) => setSelectedWorkAreaId(v === 'all' ? '' : Number(v))}
              >
                <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                  <SelectValue placeholder="All work areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All work areas</SelectItem>
                  {workAreas.map((wa) => (
                    <SelectItem key={wa.id} value={String(wa.id)}>
                      {wa.workAreaName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
             <div className="lg:col-span-3 flex items-center gap-2">
               <Button 
                 onClick={handleSearch}
                 disabled={isLoading}
                 className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
               >
                 {isLoading ? (
                   <div className="flex items-center gap-2">
                     <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                     Searching...
                   </div>
                 ) : (
                   <>
                     <Search className="h-4 w-4 mr-2" />
                     Search
                   </>
                 )}
               </Button>
               
               <Button 
                 variant="outline" 
                 onClick={handleReset}
                 className="border-primary-200 text-primary-700 hover:bg-primary-50"
               >
                 <RotateCcw className="h-4 w-4 mr-2" />
                 Reset
               </Button>
             </div>
          </div>
          
          {/* Selected Filters Display */}
          {(selectedEndGoods.length > 0 || selectedWorkAreaId !== '') && (
            <div className="mt-4 pt-4 border-t border-primary-200">
              <div className="flex flex-wrap gap-2">
                {/* Selected Work Area */}
                {selectedWorkAreaId !== '' && (() => {
                  const wa = workAreas.find(w => w.id === selectedWorkAreaId);
                  return wa ? (
                    <Badge
                      key="work-area"
                      variant="outline"
                      className="border-primary-200 text-primary-700 bg-primary-50"
                    >
                      Work area: {wa.workAreaName}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => setSelectedWorkAreaId('')}
                      />
                    </Badge>
                  ) : null;
                })()}
                
                {/* Selected End Goods */}
                {selectedEndGoods.map((endGoodId) => {
                  const endGood = endGoods.find(eg => eg.id === endGoodId);
                  return endGood ? (
                    <Badge
                      key={endGoodId}
                      variant="outline"
                      className="border-primary-200 text-primary-700 bg-primary-50"
                    >
                      {endGood.endgoodName}
                      <X
                        className="h-3 w-3 ml-1 cursor-pointer"
                        onClick={() => removeEndGoodFilter(endGoodId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      <Card className="border-primary-200 shadow-lg shadow-primary-100/50">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-primary-800">
                <FileText className="h-5 w-5 text-primary-700" />
                WIP Tasks ({filteredTasks.length} found)
              </CardTitle>
              <CardDescription>
                {selectedTasks.length > 0 && `${selectedTasks.length} task(s) selected`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedTasks.length > 0 && (
                <Button
                  onClick={() => {
                    // Get selected task details by mapping keys back to actual tasks
                    const selectedTaskDetails = selectedTasks.map(key => {
                      const parts = key.split('-');
                      const workOrderId = parseInt(parts[0]);
                      const processStepId = parseInt(parts[1]);
                      const index = parseInt(parts[2]);
                      
                      // Find the task from paginatedTasks using the index
                      const task = paginatedTasks[index];
                      
                      // Ensure bomHeaderId is included (should already be in WorkInProgressSummary)
                      if (task) {
                        // console.log(`📦 Task ${task.workOrderNumber} - bomHeaderId: ${task.bomHeaderId}`);
                        return {
                          ...task,
                          bomHeaderId: task.bomHeaderId // Explicitly include bomHeaderId
                        };
                      }
                      return null;
                    }).filter(Boolean) as WorkInProgressSummary[]; // Remove any undefined entries
                    
                    console.log(`✅ Sending ${selectedTaskDetails.length} tasks with bomHeaderIds:`, 
                      selectedTaskDetails.map(t => ({ workOrderNumber: t.workOrderNumber, bomHeaderId: t.bomHeaderId }))
                    );
                    
                    // Navigate to registration page with selected tasks (including bomHeaderId)
                    navigate('/work-order-registration', { 
                      state: { selectedTasks: selectedTaskDetails } 
                    });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Register ({selectedTasks.length})
                </Button>
              )}
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                className="border-primary-200 text-primary-700 hover:bg-primary-50"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={paginatedTasks.length > 0 && paginatedTasks.every((task, index) => selectedTasks.includes(`${task.workOrderId}-${task.processStepId}-${index}`))}
                      onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                      className="border-primary-300"
                    />
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('workOrderNumber')}
                  >
                    <div className="flex items-center gap-2">
                      Work Order No
                      {getSortIcon('workOrderNumber')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('productionDate')}
                  >
                    <div className="flex items-center gap-2">
                      Production Date
                      {getSortIcon('productionDate')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('salesOrderNumber')}
                  >
                    <div className="flex items-center gap-2">
                      Sales Order
                      {getSortIcon('salesOrderNumber')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('endgoodName')}
                  >
                    <div className="flex items-center gap-2">
                      End Good Name
                      {getSortIcon('endgoodName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('processName')}
                  >
                    <div className="flex items-center gap-2">
                      Process Name
                      {getSortIcon('processName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('wipQuantity')}
                  >
                    <div className="flex items-center gap-2">
                      WIP Qty
                      {getSortIcon('wipQuantity')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('nextProcessName')}
                  >
                    <div className="flex items-center gap-2">
                      Next Process
                      {getSortIcon('nextProcessName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-primary-100/50 select-none"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center gap-2">
                      Priority
                      {getSortIcon('priority')}
                    </div>
                  </TableHead>
                  {/* <TableHead>Actions</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTasks.map((task, index) => (
                  <TableRow 
                    key={`${task.workOrderId}-${task.processStepId}-${index}`} 
                    className={`bg-white hover:bg-primary-50/50 ${
                      selectedTasks.includes(`${task.workOrderId}-${task.processStepId}-${index}`) ? 'bg-primary-100/30' : ''
                    } ${task.isOverdue ? 'bg-red-50 border-red-200' : ''}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedTasks.includes(`${task.workOrderId}-${task.processStepId}-${index}`)}
                        onCheckedChange={(checked) => {
                          // console.log('Individual checkbox clicked:', { taskKey: `${task.workOrderId}-${task.processStepId}-${index}`, checked });
                          handleTaskSelect(`${task.workOrderId}-${task.processStepId}-${index}`, checked as boolean);
                        }}
                        className="border-primary-300"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {task.workOrderNumber}
                        {task.isOverdue && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const prodDate = (task as any).productionDate || task.productionDate;
                        return prodDate 
                          ? new Date(prodDate).toLocaleDateString() 
                          : 'N/A';
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-purple-200 text-purple-700">
                        {task.salesOrderNumber}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{task.endgoodName}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary-500" />
                        {task.processName}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{task.wipQuantity}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        {task.nextProcessName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        task.isOverdue ? 'bg-red-100 text-red-800 hover:bg-red-200' :
                        task.isReadyForProduction ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                        'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      }>
                        {task.isOverdue ? 'Critical' : 
                         task.isReadyForProduction ? 'On Track' : 'Delayed'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getPriorityBadge(task.priority)}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                    {/* <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewWorkOrder(task.workOrderId)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditWorkOrder(task.workOrderId)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {sortedTasks.length > tasksPerPage && (
            <div className="p-4 border-t border-primary-200 bg-gradient-to-r from-primary-50/30 to-primary-100/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(startIndex + tasksPerPage, sortedTasks.length)} of {sortedTasks.length} tasks
                  </span>
                  <Select value={tasksPerPage.toString()} onValueChange={(value) => setTasksPerPage(parseInt(value))}>
                    <SelectTrigger className="w-20 border-primary-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
                
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}